import { Component, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { BatchAddArgs, BeforeBatchAddArgs, BeforeBatchSaveArgs, CellSaveArgs, EditSettingsModel, GridComponent, SelectionSettingsModel, GridModule } from '@syncfusion/ej2-angular-grids';
import { ToastService } from 'src/app/base/services/toast.service';
import { IFieldValueReplacementEntry, IValueChangedInfo } from './field-value-replacement-list-editor.interfaces';
import { QueryBuilderModule } from '@syncfusion/ej2-angular-querybuilder';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';


@Component({
    selector: 'app-field-value-replacement-list-editor',
    templateUrl: './field-value-replacement-list-editor.component.html',
    styleUrls: ['./field-value-replacement-list-editor.component.scss'],
    imports: [GridModule, ToolbarModule, QueryBuilderModule, TranslateModule]
})
export class FieldValueReplacementListEditorComponent {
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);

  public readonly valueReplacementGrid = viewChild<GridComponent>(GridComponent);

  public readonly fieldValueReplacements = input<IFieldValueReplacementEntry[] | null>(null);

  public readonly sourceValueDeleted = output<number>();
  public readonly linesCreated = output<IFieldValueReplacementEntry[]>();
  public readonly sourceValueChanged = output<IValueChangedInfo>();
  public readonly replacementValueChanged = output<IValueChangedInfo>();

  public readonly fieldsGridCommands = [{ type: 'Delete', buttonOption: { cssClass: 'e-flat', iconCss: 'e-delete e-icons' } }];
  public readonly editSettings: EditSettingsModel = { allowEditing: true, allowAdding: true, allowDeleting: true, mode: 'Batch', newRowPosition: 'Bottom', showConfirmDialog: false };
  public readonly selectionOptions: SelectionSettingsModel = { type: 'Multiple', mode: 'Cell', cellSelectionMode: 'Box' };
  public readonly valueGridToolBar = [{
    text: this.translate.instant('i18n.domain.export-configuration.field-value-replacements.field-replacement-grid.paste-button'),
    tooltipText: this.translate.instant('i18n.domain.export-configuration.field-value-replacements.field-replacement-grid.paste-button-tooltip'), prefixIcon: 'e-paste', id: 'paste-data'
  }];

  private lowestId: number = 0;
  private readonly isGridReady = signal(false);

  public onGridCreated(): void {
    this.isGridReady.set(true);
  }

  constructor() {
    effect(() => {
      const value = this.fieldValueReplacements();
      const grid = this.valueReplacementGrid(); // tracked: re-runs when grid becomes available after view init
      const ready = this.isGridReady();         // tracked: re-runs after Syncfusion fires (created)
      untracked(() => {
        this.lowestId = 0;
        if (grid == null || !ready) {
          return; // wait until Syncfusion has fully initialised all child components (toolbar etc.)
        }
        if (value == null) {
          grid.dataSource = [];
          setTimeout(() => grid.refresh(), 0);
          return;
        }
        value.forEach((entry) => {
          if (entry.id < this.lowestId) {
            this.lowestId = entry.id;
          }
        });
        if (value.length === 0) {
          value.push({ id: this.getNewId(), sourceValue: null, replacementValue: null, origSourceValue: null, origReplacementValue: null });
        }
        setTimeout(() => grid.refresh(), 0);
      });
    });
  }

  public onCellSaved(args: CellSaveArgs) {
    if (args.value == null || args.value.trim() == "") {
      if (!args.cell?.classList.contains('e-invalid-value')) {
        args.cell?.classList.add('e-invalid-value');
      }
    } else {
      args.cell?.classList.remove('e-invalid-value');
    }

    const data = args.rowData as IFieldValueReplacementEntry;
    if (args.columnName == 'sourceValue') {
      this.sourceValueChanged.emit({ id: data.id, newValue: args.value ?? '' });
      return;
    }
    if (args.columnName == 'replacementValue') {
      this.replacementValueChanged.emit({ id: data.id, newValue: args.value ?? '' });
      return;
    }
  }

  public onBeforeBatchAdd(args: BeforeBatchAddArgs) {
    (args.defaultData as any).id = this.getNewId();
  }

  public onBatchAdd(args: BatchAddArgs) {
    const entry = args.defaultData as IFieldValueReplacementEntry;
    this.fieldValueReplacements()?.push(entry);
  }

  public onBeforeBatchSave(args: BeforeBatchSaveArgs) {
    // we added entry already on batch add -> reason is, that before batch save is not always triggered, but batch add is
    args.cancel = true;
  }

  public onValueReplacementGridLoad(args: any) {
    const grid = this.valueReplacementGrid() as any;
    grid.element.addEventListener('mouseup', (e: MouseEvent) => {
      if ((e.target as HTMLElement).classList.contains("e-rowcell")) {
        if (grid.isEdit)
          grid.endEdit();
        let index: number = parseInt(((e.target as HTMLElement) as any).getAttribute("Index"));
        grid.selectRow(index);
        grid.startEdit();
      };
    });
  }

  public valueReplacementGridRowDataBound(args: any) {
    const grid = this.valueReplacementGrid()!;
    // change the color of new rows
    if (args.data.id <= 0 || args.data.id == null) {
      args.row.classList.add('e-insertedrow');
      // for each cell apply e-updatedtd class
      for (var i = 0; i < args.row.cells.length; i++) {
        args.row.cells[i].classList.add('e-updatedtd');
      }
    } else {
      if (args.data.origSourceValue != args.data.sourceValue) {
        const sourceColumnIndex = grid.getColumnByField('sourceValue').index;
        args.row.cells[sourceColumnIndex].classList.add('e-updatedtd');
      }

      if (args.data.origReplacementValue != args.data.replacementValue) {
        const replacementColumnIndex = grid.getColumnByField('replacementValue').index;
        args.row.cells[replacementColumnIndex].classList.add('e-updatedtd');
      }
    }

    if (args.data.sourceValue == null || args.data.sourceValue.trim() == "") {
      const sourceColumnIndex = grid.getColumnByField('sourceValue').index;
      args.row.cells[sourceColumnIndex].classList.add('e-invalid-value');
    }

    if (args.data.replacementValue == null || args.data.replacementValue.trim() == "") {
      const replacementColumnIndex = grid.getColumnByField('replacementValue').index;
      args.row.cells[replacementColumnIndex].classList.add('e-invalid-value');
    }
  }

  public valueReplacementGridCommandClicked(args: any): void {
    if (args.commandColumn.type == 'Delete') {
      const entries = this.fieldValueReplacements();
      const index = entries?.findIndex(f => f.id == args.rowData.id) ?? -1;
      if (index >= 0) {
        entries!.splice(index, 1);
        this.sourceValueDeleted.emit(args.rowData.id);
        this.valueReplacementGrid()?.refresh();
      }
    }
  }

  public valueReplacementToolClicked(args: any): void {
    if (args.item.id === 'paste') {
      this.pasteFromClipBoard(args.originalEvent);
    }
  }

  private pasteFromClipBoard(event: ClipboardEvent) {
    const entries = this.fieldValueReplacements();
    if (entries == null) {
      return;
    }

    navigator['clipboard'].readText().then((data) => {
      var rows = data.split('\n');
      if (rows.length == 0) {
        return;
      }
      var cols: string | any[];
      var createdRows: number = 0;
      var updatedRows: number = 0;
      const newLines: IFieldValueReplacementEntry[] = [];
      rows.forEach((row) => {
        cols = row.split('\t');
        if (cols.length > 0) {
          const sourceValue = cols[0]?.trim();
          const replacementValue = cols[1]?.trim();
          // Check if first column is empty then skip
          if (sourceValue == "" || sourceValue == null) {
            return;
          }

          // If entries does not have the source value then add it else update the replacement value
          var existingReplacement = entries.find(f => f.sourceValue == sourceValue);
          if (existingReplacement) {
            existingReplacement.replacementValue = replacementValue;
            updatedRows++;
            return;
          }

          createdRows++;
          const replacementEntry = { id: this.getNewId(), sourceValue, replacementValue, origSourceValue: null, origReplacementValue: null };
          entries.push(replacementEntry);
          newLines.push(replacementEntry);
        }
      });

      if (newLines.length > 0) {
        this.linesCreated.emit(newLines);
      }

      if (createdRows == 0) {
        return;
      }

      // Show number of rows added in toast
      this.toastService.showSuccess(
        'i18n.domain.export-configuration.field-value-replacements.field-replacement-grid.paste-success-title',
        this.translate.instant('i18n.domain.export-configuration.field-value-replacements.field-replacement-grid.paste-success-message', { totalRows: createdRows, createdRows, updatedRows }));

      this.valueReplacementGrid()?.refresh();
    });
  }

  private getNewId(): number {
    return --this.lowestId;
  }
}
